import { useMotionValue, type MotionValue } from 'motion/react';
import { getLeftOffset, getTopOffset } from './useRectTracker';
import { useLenis } from '../LenisScroll';
import { mapLinear } from './mapLinear';

/**
 * The useProgressTracker function is a custom hook that returns a Framer Motion value bound to a tracker scrollState.
 * It takes in a tracker object of type Rect and an optional margin object with properties top, right, bottom, and left.
 * The track represents a reference to the DOM element that you want to track,
 * and the margin object specifies additional margins around the tracked element.
 *
 * This hook can be used to track the progress of an element within a scrollable container and update the progress value accordingly.
 *
 * @param {React.RefObject<HTMLElement>} track (required): The element to track the progress of.
 * @param {{top?: number, right?: number, bottom?: number, left?: number}} margin Optional margin settings for calculating visibility within the scrollable container. Defaults to `{ top: 0, right: 0, bottom: 0, left: 0 }`.
 * A value of 1 is equivalent to the viewport height (width for horizontal values). Positive values increase the frame rectangle, negative values decrease.
 * @returns {MotionValue} The function returns a MotionValue object from the motion library.
 * This MotionValue object represents the progress of the tracked element and can be used to animate UI elements based on the progress.
 */

function useProgressTracker(
	track: React.RefObject<HTMLElement>,
	margin?: {
		top?: number;
		right?: number;
		bottom?: number;
		left?: number;
	},
): MotionValue {
	const progress = useMotionValue(0);
	const lenis = useLenis();

	lenis?.on('scroll', latest => {
		if (latest.animatedScroll > 0 && latest.rootElement && track.current) {
			if (lenis.isHorizontal && latest.options.wrapper && 'clientWidth' in latest.options.wrapper) {
				const wrapperWidth = Number(latest.options.wrapper.clientWidth);
				const marginLeft = wrapperWidth * (margin?.left || 0);
				const marginRight = wrapperWidth * (margin?.right || 0);

				const left = getLeftOffset(track.current) - lenis['animatedScroll'] + marginLeft * -1;

				const pxInside = wrapperWidth - left;
				const currentProgress = mapLinear(pxInside, 0, wrapperWidth + (marginLeft + marginRight) + track.current.clientWidth, 0, 1); // percent of total visible distance

				progress.set(currentProgress);
			} else {
				if (latest.options.wrapper && 'clientHeight' in latest.options.wrapper) {
					const wrapperHeight = Number(latest.options.wrapper.clientHeight);
					const marginTop = wrapperHeight * (margin?.top || 0);
					const marginBottom = wrapperHeight * (margin?.bottom || 0);
					const top = getTopOffset(track.current) - lenis['animatedScroll'] + marginTop * -1;

					const pxInside = wrapperHeight - top;
					const currentProgress = mapLinear(pxInside, 0, wrapperHeight + (marginTop + marginBottom) + track.current.clientHeight, 0, 1); // percent of total visible distance

					progress.set(currentProgress);
				}
			}
		}
	});

	return progress;
}

export { useProgressTracker };
